import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

/**
 * The React counterpart of the brand "Motion: fluid, never snap" primitive
 * (marketing-website `lib/sliding-indicator.ts`). A single pill element slides
 * to whichever item is active instead of each item repainting its own
 * background on and off. The pill eases its position AND size to the active
 * item over the brand slide duration on the brand curve, so a horizontal
 * segmented control slides right/left.
 *
 * Per brand motion rule, a moving element animates `transform` only, never an
 * animated `width`/`height` (touching layout mid-move hitches the first frame).
 * So the pill is sized ONCE to the largest item, then every move is a pure
 * translate + scale. `prefers-reduced-motion` is honoured (jumps without
 * animating) and the pill re-measures on resize.
 */
export interface SlidingIndicator<Key extends string> {
  /** Attach to the positioned track that contains the items and the pill. */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Inline style to spread onto the pill element. */
  pillStyle: CSSProperties;
  /** Ref callback that registers an item under `key` so the pill can track it. */
  register: (key: Key) => (el: HTMLElement | null) => void;
}

/**
 * Track the `activeKey` item within a container and expose a pill style that
 * eases onto it.
 *
 * @param activeKey - The key of the currently selected item.
 * @returns Refs and the computed pill style. See {@link SlidingIndicator}.
 */
export function useSlidingIndicator<Key extends string>(activeKey: Key): SlidingIndicator<Key> {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef(new Map<Key, HTMLElement>());
  const placedRef = useRef(false);
  const [pillStyle, setPillStyle] = useState<CSSProperties>({
    position: 'absolute',
    top: 0,
    left: 0,
    transformOrigin: '0 0',
    opacity: 0,
    pointerEvents: 'none',
  });

  const register = useCallback(
    (key: Key) => (el: HTMLElement | null) => {
      if (el) itemRefs.current.set(key, el);
      else itemRefs.current.delete(key);
    },
    [],
  );

  /** Size the pill to the largest item and ease (or jump) it onto the active one. */
  const place = useCallback(
    (animate: boolean) => {
      const active = itemRefs.current.get(activeKey);
      if (!active || !containerRef.current) return;

      const items = [...itemRefs.current.values()];
      const baseW = Math.max(1, ...items.map((i) => i.offsetWidth));
      const baseH = Math.max(1, ...items.map((i) => i.offsetHeight));

      setPillStyle((prev) => {
        // A hidden control (display:none) reports 0: keep the pill invisible so
        // it doesn't park at the origin and flash when the control reappears.
        if (active.offsetWidth === 0 && active.offsetHeight === 0) {
          return { ...prev, opacity: 0 };
        }
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const sx = active.offsetWidth / baseW;
        const sy = active.offsetHeight / baseH;
        return {
          ...prev,
          width: baseW,
          height: baseH,
          // translate3d keeps the pill on its own compositor layer; scale fits
          // it to the active item, so only transform animates.
          transform: `translate3d(${active.offsetLeft}px, ${active.offsetTop}px, 0) scale(${sx}, ${sy})`,
          transition: animate && !reduce ? 'transform var(--dur-slide) var(--ease)' : 'none',
          opacity: 1,
        };
      });
    },
    [activeKey],
  );

  // First placement jumps into position; every later selection slides.
  useLayoutEffect(() => {
    place(placedRef.current);
    placedRef.current = true;
  }, [activeKey, place]);

  // Re-measure on resize without animating.
  useEffect(() => {
    const onResize = () => place(false);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [place]);

  return { containerRef, pillStyle, register };
}
